import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/lib/api";
import { Download, CalendarRange } from "lucide-react";

// Compact PDF-report menu with 4 presets + custom range.
// Usage: <PdfReportMenu patientId={id} patientEmail={email} label="Informe PDF" />
export default function PdfReportMenu({
  patientId,
  patientEmail = "patient",
  label = "Informe PDF",
  variant = "default",
  size = "default",
  className = "rounded-full",
  testIdPrefix = "pdf",
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const daysAgo = (n) => {
    const d = new Date(today);
    d.setDate(d.getDate() - n);
    return iso(d);
  };
  const [dateFrom, setDateFrom] = useState(daysAgo(30));
  const [dateTo, setDateTo] = useState(iso(today));

  const download = async ({ from, to, tag = "" }) => {
    setBusy(true);
    try {
      const token = localStorage.getItem("sc_access_token");
      const qs = new URLSearchParams({ patient_id: patientId });
      if (from) qs.set("date_from", from);
      if (to) qs.set("date_to", to);
      const r = await fetch(`${API}/export/report.pdf?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const detail = await r.text();
        throw new Error(detail);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const range = tag || (from || to ? `${from || "inicio"}_${to || "hoy"}` : "todo");
      a.download = `report_${patientEmail}_${range}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setOpen(false);
    } catch (e) {
      alert(`No se pudo generar el informe: ${e.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={className}
          data-testid={`${testIdPrefix}-menu-trigger`}
          disabled={busy}
        >
          <Download size={size === "sm" ? 14 : 16} className="mr-1" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3 space-y-2" data-testid={`${testIdPrefix}-menu-content`}>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
          <CalendarRange size={12} /> Rango del informe
        </p>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start"
          onClick={() => download({ tag: "todo" })}
          disabled={busy}
          data-testid={`${testIdPrefix}-preset-all`}
        >
          Toda la actividad
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start"
          onClick={() => download({ from: daysAgo(7), to: iso(today), tag: "7d" })}
          disabled={busy}
          data-testid={`${testIdPrefix}-preset-7d`}
        >
          Última semana (7 días)
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start"
          onClick={() => download({ from: daysAgo(30), to: iso(today), tag: "30d" })}
          disabled={busy}
          data-testid={`${testIdPrefix}-preset-30d`}
        >
          Último mes (30 días)
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start"
          onClick={() => download({ from: daysAgo(90), to: iso(today), tag: "90d" })}
          disabled={busy}
          data-testid={`${testIdPrefix}-preset-90d`}
        >
          Últimos 3 meses (90 días)
        </Button>
        <div className="pt-2 border-t border-border">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            Rango libre
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="pdf-from" className="text-xs">Desde</Label>
              <Input
                id="pdf-from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-8 text-xs"
                data-testid={`${testIdPrefix}-date-from`}
              />
            </div>
            <div>
              <Label htmlFor="pdf-to" className="text-xs">Hasta</Label>
              <Input
                id="pdf-to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-8 text-xs"
                data-testid={`${testIdPrefix}-date-to`}
              />
            </div>
          </div>
          <Button
            size="sm"
            className="w-full mt-2 rounded-full"
            onClick={() => download({ from: dateFrom, to: dateTo })}
            disabled={busy || !dateFrom || !dateTo || dateFrom > dateTo}
            data-testid={`${testIdPrefix}-custom-download`}
          >
            {busy ? "Generando…" : "Descargar rango"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
